import * as React from "react";
import { Button as OldButton, Checkbox, Input, Message, Modal, Popup } from "semantic-ui-react";
import { OauthApplicationServices as ApiKeyServices } from "../../services/bedrock/OauthApplicationServices";
import { OauthApplication as ApiKey } from "../../services/bedrock/dtos/OauthApplication";
import copy from "copy-to-clipboard";
import { useCallback, useEffect, useMemo } from "react";
import { Button, Field } from "@/components/ui";
import { toast } from "sonner";

interface SaveApiKeyModalProps {
  open: boolean;
  apiKey?: ApiKey;
  onClose: (savedApiKey?: ApiKey) => void;
}

export const SaveApiKeyModal: React.FC<SaveApiKeyModalProps> = ({ open, apiKey, onClose }) => {
  const [name, setName] = React.useState<string>("");
  const [saving, setSaving] = React.useState<boolean>(false);
  const [savedApiKey, setSavedApiKey] = React.useState<ApiKey>();
  const [storedSafelyChecked, setStoredSafelyChecked] = React.useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setSaving(false);
      setSavedApiKey(undefined);
      setStoredSafelyChecked(false);
    }
  }, [open]);

  useEffect(() => setName(apiKey?.name || ""), [apiKey]);

  const handleSaveClicked = () => {
    saveApiKey();
  };

  const handleCopyClicked = (text: string) => {
    copy(text);
    toast.success("Copied to clipboard");
  };

  const handleRenewSecretClicked = () => {
    if (!window.confirm("Renewing the secret will invalidate the current token. Are you sure you want to continue?")) {
      return;
    }
    saveApiKey(true);
  };

  const handleDeleteClicked = useCallback(() => {
    if (!apiKey) return;
    if (!window.confirm("Are you sure you want to delete this API key?")) {
      return;
    }
    setSaving(true);
    ApiKeyServices.delete(apiKey.id)
      .then(() => onClose(apiKey))
      .catch(console.error);
  }, [apiKey]);

  const saveApiKey = (renewSecret: boolean = false) => {
    setSaving(true);
    var promise: Promise<ApiKey>;
    if (apiKey) {
      if (renewSecret) {
        promise = ApiKeyServices.renewSecret(apiKey.id);
      } else {
        promise = ApiKeyServices.update(apiKey.id, name);
      }
    } else {
      promise = ApiKeyServices.create(name, ["read", "write"]);
    }
    promise
      .then(response => {
        if (apiKey && !renewSecret) {
          onClose(response);
        } else {
          setSavedApiKey(response);
        }
      })
      .catch(console.error)
      .finally(() => setSaving(false));
  };

  const downloadApiKey = (apiKey: ApiKey) => {
    const json = {
      name: apiKey.name,
      clientId: apiKey.uid,
      clientSecret: apiKey.plaintextSecret,
    };
    const blob = new Blob([JSON.stringify(json)], { type: "application/json" });
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = "api-key.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(href);
  };

  const canSave = () => {
    return name.length > 0 && apiKey?.name !== name;
  };

  const effectiveApiKey = useMemo(() => {
    return savedApiKey || apiKey;
  }, [savedApiKey, apiKey]);

  const cancel = () => onClose();

  return (
    <Modal
      open={open}
      className="saveApiKeyModal"
      onClose={cancel}
      size="tiny"
      closeOnEscape={false}
      closeOnDimmerClick={false}
    >
      <Modal.Header>{apiKey ? "Update" : "Create"} API Key</Modal.Header>
      <Modal.Content className="flex flex-col gap-3">
        <Field label="Name">
          <Input
            fluid
            placeholder="Name..."
            value={name}
            disabled={saving}
            readOnly={!!savedApiKey}
            onChange={e => setName(e.target.value)}
          />
        </Field>
        {effectiveApiKey && (
          <>
            <Field label="Client ID">
              <Input
                fluid
                value={effectiveApiKey.uid}
                readOnly
                action={
                  <OldButton primary icon="copy outline" onClick={() => handleCopyClicked(effectiveApiKey.uid || "")} />
                }
              />
            </Field>
            <Field label="Client Secret">
              <Input
                fluid
                value={effectiveApiKey.plaintextSecret || "•••••••••••••••••••••••••••••••••••••••••••"}
                readOnly
                action={
                  effectiveApiKey.plaintextSecret ? (
                    <OldButton
                      primary
                      icon="copy outline"
                      onClick={() => handleCopyClicked(effectiveApiKey.plaintextSecret || "")}
                    />
                  ) : (
                    <Popup
                      trigger={<OldButton primary icon="refresh" onClick={() => handleRenewSecretClicked()} />}
                      content="The client secret can only be viewed when it is first created. If you have lost it or need to renew it, you can do so here."
                    />
                  )
                }
              />
            </Field>
          </>
        )}
        {savedApiKey && (
          <>
            <Message
              warning
              icon="warning circle"
              header="Important"
              content="Please store this information somewhere safe. Once you have closed this screen, you will no longer be able to view the Client Secret."
              size="small"
              className="m-0"
            />
            <Button children="Download" onClick={() => downloadApiKey(savedApiKey)} />
          </>
        )}
      </Modal.Content>
      <Modal.Actions className="flex items-center gap-2">
        {!savedApiKey ? (
          <>
            {apiKey && (
              <Button variant="destructive" children="Delete" disabled={saving} onClick={handleDeleteClicked} />
            )}
            <div className="grow" />
            <Button variant="outline" children="Cancel" disabled={saving} onClick={cancel} />
            <Button
              children={apiKey ? "Update" : "Create"}
              disabled={!canSave()}
              loading={saving}
              onClick={handleSaveClicked}
            />
          </>
        ) : (
          <>
            <Checkbox
              checked={storedSafelyChecked}
              label="I have stored this information somewhere safe"
              onChange={() => setStoredSafelyChecked(!storedSafelyChecked)}
            />
            <div className="grow" />
            <Button children="Done" disabled={!storedSafelyChecked} onClick={() => onClose(savedApiKey)} />
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
};
